import { ArrowRight, GitMerge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

export interface EntityResolutionCardViewProps {
  /** Number of pending potential-duplicate matches. */
  pending: number
  isLoading: boolean
  isError: boolean
  /** Destination for the Resolve/Review CTA. */
  linkTo: string
}

/**
 * Pure presentation for the overview's entity-resolution callout. All data
 * (pending count + load state) is supplied by the connected card in
 * `features/case-overview`.
 */
export function EntityResolutionCardView({
  pending,
  isLoading,
  isError,
  linkTo,
}: EntityResolutionCardViewProps) {
  const { t } = useTranslation()

  const hasPending = pending > 0
  const title = t('overview.entityResolution.title', 'Entity resolution')
  const summary = isLoading
    ? t('overview.entityResolution.loading', 'Checking for potential duplicates…')
    : isError
      ? t('overview.entityResolution.error', 'Could not load potential duplicates right now')
      : hasPending
        ? t('overview.entityResolution.summary', {
            defaultValue: '{{count}} potential duplicates waiting for review',
            count: pending,
          })
        : t('overview.entityResolution.empty', 'No outstanding matches — entities look clean')
  const ctaLabel = hasPending
    ? t('overview.entityResolution.resolve', 'Resolve')
    : t('overview.entityResolution.review', 'Review')

  return (
    <Card className="p-4 md:p-6">
      {/* Phone: the CTA drops to its own full-width row so the copy keeps the line. */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 md:flex-nowrap md:gap-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
          <GitMerge className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{summary}</p>
        </div>
        <Button asChild variant="outline" size="xs" className="shrink-0 max-md:h-9 max-md:w-full">
          <Link to={linkTo}>
            {ctaLabel}
            <ArrowRight className="size-3" />
          </Link>
        </Button>
      </div>

      {hasPending && (
        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <GitMerge className="size-3.5" />
            <span className="font-mono tabular-nums text-foreground">{pending}</span>
            <span>{t('overview.entityResolution.pendingLabel', 'pending matches')}</span>
          </span>
        </div>
      )}
    </Card>
  )
}
